///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { Component, ElementRef, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { MatDialogRef } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { PARAM_BBOX } from '../../../../shared/constants/url-params.constants';
import { ProjectInteractiveWindowsPopupService } from '../../project-interactive-windows-popup.service';
import { AbstractInteractiveWindow } from '../../abstract-interactive-window';
import { GtthTunnelsService } from './tunnels.service';
import { TunnelModel } from './models/tunnel.model';
import { TunnelTypeEnum } from '../../../../generated/shared-types.model';
import { TunnelsWithTypeModel } from './models/tunnels-with-type.model';
import { ExtendedTunnelReportDTO } from './models/extended-tunnel-report.model';
import { TunnelListType } from './models/tunnel-list-type';

@Component({
    selector: 'winde-tunnels-report',
    templateUrl: 'tunnels-report.component.html',
    styleUrls: ['tunnels-report.component.scss', '../../project-windows.scss'],
    standalone: false
})
export class GtthTunnelsReportWindowComponent extends AbstractInteractiveWindow implements OnInit {
    public searchString: string;
    public tunnelListType = TunnelListType;
    public showBuLocationsWithSingleTunnel: boolean;
    public showBuLocationsWithoutTunnels: boolean;

    tunnelsToShow$: Observable<TunnelsWithTypeModel[]>;
    locationsToShow$: Observable<ExtendedTunnelReportDTO> = this.tunnelsService.getLocationNames();

    constructor(
        private readonly elementRef: ElementRef,
        public dialogRef: MatDialogRef<GtthTunnelsReportWindowComponent>,
        public tunnelsService: GtthTunnelsService
    ) {
        super(elementRef);
    }

    ngOnInit() {
        this.tunnelsToShow$ = this.tunnelsService
            .getTunnelsWithTypes()
            .pipe(map((tunnels: TunnelModel[]) => this.convertTunnelsToTunnelsWithTypes(tunnels)));

        this.filterChanged('');
    }

    toggleOpenedLists(list: TunnelListType) {
        switch (list) {
            case TunnelListType.WITH_SINGLE_TUNNELS:
                this.showBuLocationsWithoutTunnels = false;
                this.showBuLocationsWithSingleTunnel = !this.showBuLocationsWithSingleTunnel;
                break;
            case TunnelListType.WITHOUT_TUNNELS:
                this.showBuLocationsWithoutTunnels = !this.showBuLocationsWithoutTunnels;
                this.showBuLocationsWithSingleTunnel = false;
                break;
        }
    }

    filterChanged($event: any): void {
        this.searchString = $event;
        this.tunnelsService.updateFilter(this.searchString);
    }

    clear() {
        this.dialogRef.close('cancel');
    }

    designRequired(): boolean {
        return true;
    }

    isRestorable(): boolean {
        return true;
    }

    private convertTunnelsToTunnelsWithTypes(tunnels: TunnelModel[]): TunnelsWithTypeModel[] {
        const tunnelsByType = new Map<TunnelTypeEnum, TunnelModel[]>(
            Object.keys(TunnelTypeEnum).map((typeName) => [TunnelTypeEnum[typeName], []])
        );

        tunnels.forEach((tunnel) => {
            tunnelsByType.get(tunnel.tunnelType).push(tunnel);
        });

        return Object.keys(TunnelTypeEnum)
            .filter((typeName) => tunnelsByType.get(TunnelTypeEnum[typeName]).length > 0)
            .map((typeName) => new TunnelsWithTypeModel(TunnelTypeEnum[typeName], tunnelsByType.get(TunnelTypeEnum[typeName])));
    }
}

@Component({
    selector: 'winde-tunnels-report-popup',
    template: '',
    standalone: false
})
export class ProjectGtthTunnelsReportPopupComponent implements OnInit {
    constructor(
        private readonly interactiveWindowsPopupService: ProjectInteractiveWindowsPopupService,
        private readonly activatedRoute: ActivatedRoute
    ) {}

    ngOnInit() {
        let bboxParams;
        if (this.activatedRoute.snapshot.queryParamMap.has(PARAM_BBOX)) {
            bboxParams = this.activatedRoute.snapshot.queryParamMap
                .get(PARAM_BBOX)
                .split(',')
                .map((value) => parseFloat(value));
        }
        const routerPath = this.activatedRoute.routeConfig.path;
        this.interactiveWindowsPopupService.openInteractiveWindow(
            GtthTunnelsReportWindowComponent,
            ProjectInteractiveWindowsPopupService.TUNNELS_REPORT,
            routerPath,
            bboxParams
        );
    }
}
